/**
 * @fileoverview Zod schemas for Classification Rules feature.
 * @module features/classification-rules/schemas/classificationRules
 */

import { z } from 'zod'

export const CostLinkingRequirementEnum = z.enum(['REQUIRED', 'NOT_NEEDED', 'OPTIONAL'])
export type CostLinkingRequirement = z.infer<typeof CostLinkingRequirementEnum>

/**
 * Backend response shape for a single rule.
 */
export const ClassificationRuleResponseSchema = z.object({
  id: z.coerce.number(),
  // Some environments may return non-RFC UUIDs; accept any non-empty string for robustness
  uuid: z.string(),
  orgId: z.string(),
  descriptionPattern: z.string().nullable().optional(),
  counterpartyNamePattern: z.string().nullable().optional(),
  categoryUuid: z.string().nullable().optional(),
  // Enum from BE; nullable when not set
  costLinkingRequirement: CostLinkingRequirementEnum.nullable().optional(),
  vatApplicable: z.boolean().nullable().optional(),
  reverseCharge: z.boolean().nullable().optional(),
  vatRatePercent: z.coerce.number().nullable().optional(),
  priority: z.coerce.number().nullable().optional(),
  active: z.boolean().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
})
export type ClassificationRuleResponse = z.infer<typeof ClassificationRuleResponseSchema>

/**
 * Backend request shape to create a rule.
 * Note: uses percent for VAT (e.g., 23 for 23%).
 */
export const ClassificationRuleRequestSchema = z.object({
  descriptionPattern: z.string().optional(),
  counterpartyNamePattern: z.string().optional(),
  categoryUuid: z.string().uuid().nullable().optional(),
  vatApplicable: z.boolean().nullable().optional(),
  reverseCharge: z.boolean().nullable().optional(),
  vatRatePercent: z.number().nullable().optional(),
  priority: z.number().nullable().optional(),
  active: z.boolean().nullable().optional(),
  costLinkingRequirement: CostLinkingRequirementEnum.nullable().optional(),
})
export type ClassificationRuleRequest = z.infer<typeof ClassificationRuleRequestSchema>

/**
 * Paginated response is not required for now (BE list returns array), so use array directly.
 */
export const ClassificationRuleListSchema = z.array(ClassificationRuleResponseSchema)
export type ClassificationRuleList = z.infer<typeof ClassificationRuleListSchema>
